import Cookie from 'js-cookie';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { FeatureFlags } from 'featureFlags/constants';
import { FILTER_ENUMS } from 'search/enums/constants';
import {
  GRIDS_WIDTH,
  SUPER_COMPRESSED_DESKTOP_HEADER_HEIGHT_NUMBER,
} from 'search/styles/SearchStyles';
import { GLOBAL_PRICING_ID } from 'shared/enums/GlobalPricing';
import { Grid, Loader } from 'shared/components';
import { GridHeader } from 'shared/components/Grid/GridHeader';
import { IBreadcrumb } from 'shared/types/Breadcrumbs';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { IConstructorSearchResponse } from 'shared/types/ConstructorSearchResponse';
import { MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';
import { MOBILE_HEADER_HEIGHT, NEW_NAV_MOBILE_HEADER_HEIGHT } from 'layout/header/enums/constants';
import { ScrollToTopArrow } from 'shared/components/ScrollToTopArrow';
import SearchSortAndFiltersDesktop from 'search/components/searchGrid/SearchSortAndFiltersDesktop';
import { SearchGridBreadcrumbs } from 'search/components/searchGrid/SearchGridBreadcrumbs';
import { SearchNoResults } from 'search/components/searchGrid/SearchNoResults';
import { SearchSortAndFiltersMobile } from 'search/components/searchGrid/SearchSortAndFiltersMobile';
import { TIMELINE_PATHNAME } from 'timeline/enums/constants';
import { TimelineNavigation } from 'timeline/components/TimelineNavigation';
import { getPageNumber } from 'search/utils/getPageNumber';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { nonRefElementInViewport } from 'search/utils/nonRefElementInViewport';
import { sendTrackingEvent } from 'tracking/utils';
import { showOnDesktopOnly, showOnMobileAndTabletOnly } from 'styles/utils/responsiveness';
import { toggleVisible } from 'styles/theme/animation';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { useGridScrollEvent } from 'search/hooks/useGridScrollEvent';
import { useInfiniteConstructorSearch } from 'search/hooks/useInfiniteConstructorSearch';
import {
  useBanner,
  useBranchSdk,
  useFeatureFlag,
  useIsElementInViewport,
  usePageLayoutState,
  usePutBackHeaderOnPageChange,
  useToggleHeaderOnScrollEvent,
  useWindowSize,
  useIsMounted,
} from 'shared/hooks';
import { usePersistGridScrollPosition } from 'search/hooks/usePersistGridScrollPosition';
import { useShowScrollToTopArrow } from 'search/hooks/useShowScrollToTopArrow';
import { useTimeline } from 'timeline/hooks/useTimeline';

const ScavengerHuntFoundIndicator = dynamic(
  () => import('scavengerHunt/components/ScavengerHuntFoundIndicator'),
  { ssr: false },
);

interface ISearchGrid {
  brandStoryVisible?: boolean;
  breadcrumbs?: IBreadcrumb[];
  browseKey?: string;
  browseValue?: string;
  constructorResponse: IConstructorSearchResponse | IConstructorBrowseResponse;
  description?: string;
  hasLink?: boolean;
  hideMetadata?: boolean;
  linkDestination?: string;
  linkTitle?: string;
  noIndex?: boolean;
  timelineYears?: Array<string>;
  title?: string;
}

export const SearchGrid = ({
  brandStoryVisible,
  breadcrumbs,
  browseKey,
  browseValue,
  constructorResponse,
  description,
  hasLink,
  hideMetadata = false,
  linkDestination,
  linkTitle,
  noIndex = false,
  title,
}: ISearchGrid) => {
  const observerRef = useRef();
  const shouldLoadMore = useIsElementInViewport(observerRef);
  const isMounted = useIsMounted();
  const { isMobile } = useWindowSize();
  const { isHeaderVisible } = usePageLayoutState();
  const { isBannerEnabled, title: bannerTitle, subtitle: bannerSubtitle } = useBanner();
  const isBannerVisible = isBannerEnabled && (bannerTitle || bannerSubtitle);
  const { isBranchBannerActive, branchBannerHeight } = useBranchSdk();
  const { asPath, pathname } = useRouter();
  const isSearchPath = pathname === '/search';
  const isTimelinePath = pathname === TIMELINE_PATHNAME;
  const eventTrackingEventContext = useEventContext();
  const showNoindexTag = asPath?.split('?')?.length > 1 || noIndex;
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [areBreadcrumbsVisible, setAreBreadcrumbsVisible] = useState(true);
  const [areFiltersVisible, setAreFiltersVisible] = useState(false);
  const globalPricingIdCookie = Cookie.get(GLOBAL_PRICING_ID);
  const globalPricingId = globalPricingIdCookie && JSON.parse(globalPricingIdCookie);

  useToggleHeaderOnScrollEvent(false, hideMetadata && brandStoryVisible && isMobile);
  usePutBackHeaderOnPageChange();

  const { timelineId, timelineIdType, timelineTitle, timelineDescription, timelineYears } =
    useTimeline();

  const {
    facets,
    groups,
    resultIds,
    results,
    resultsCount,
    setSize,
    size,
    sortOptions,
    searchQuery,
  } = useInfiniteConstructorSearch({
    fallbackData: [constructorResponse],
    browseKey: isTimelinePath ? timelineIdType : browseKey,
    browseValue: isTimelinePath ? timelineId : browseValue,
    globalPricingId,
  });

  const priceFacet = facets?.find((facet) => facet.name === FILTER_ENUMS.PRICE);
  const currencyRates = priceFacet?.data?.currency_rates || null;
  const isScavengerHuntEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SCAVENGER_HUNT);

  const { alwaysCompressHeader, superCompressHeader } = useGridScrollEvent({
    compare: [title, searchQuery],
    alwaysCompressHeader:
      isSearchPath || isTimelinePath || !description || description?.length <= 0 || hideMetadata,
  });

  // watch for click on filter, check height of desktop
  // if filters extended on desktop, hide  breadcrumbs
  const handleClickInsidePage = (e) => {
    const attrValue = e.target.getAttribute('data-qa');

    if (attrValue?.includes('filter') && document) {
      const desktopFilters = document.querySelector<HTMLElement>('#desktop-filters');
      const desktopFiltersHeight = desktopFilters?.offsetHeight;
      if (isMounted()) {
        setAreBreadcrumbsVisible(desktopFiltersHeight < 400);
      }
    }
  };

  usePersistGridScrollPosition({ setSize });
  const showScrollToTopArrow = useShowScrollToTopArrow([JSON.stringify(results)]);

  useEffect(() => {
    if (shouldLoadMore && resultsCount > results.length) {
      void setSize(Number(size) + 1);
    }
  }, [shouldLoadMore]);

  useEffect(() => {
    sendTrackingEvent('CONSTRUCTOR_SEE_MORE_SCROLL', {
      event_context: eventTrackingEventContext,
      page_number: getPageNumber(results.length),
    });
  }, [results.length]);

  useEffect(() => {
    const handleScrollEvent = () => {
      setTimeout(() => {
        if (isMounted()) {
          if (!isMobile) {
            const desktopFilters = document.querySelector<HTMLElement>('#desktop-filters');
            const desktopFiltersHeight = desktopFilters?.offsetHeight;
            setAreBreadcrumbsVisible(desktopFiltersHeight < 400);

            // get the position of the footer, and when it's in view
            // make sure the breadcrumbs are no longer fixed, but absolute
            const siteFooter = document.querySelector<HTMLElement>('#site-footer');
            const footerStatus = nonRefElementInViewport(siteFooter, 58);
            setIsFooterVisible(footerStatus);
          }

          const bodyDistanceFromTop = document.querySelector<HTMLElement>('#grid-body')?.offsetTop;
          const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

          if (
            bodyDistanceFromTop - SUPER_COMPRESSED_DESKTOP_HEADER_HEIGHT_NUMBER <=
            currentScroll
          ) {
            setAreFiltersVisible(true);
          } else {
            setAreFiltersVisible(false);
          }
        }
      }, 300);
    };

    document.addEventListener('scroll', handleScrollEvent);

    return () => {
      document.removeEventListener('scroll', handleScrollEvent);
    };
  });

  return (
    <SearchGrid.Container
      data-qa="search_grid_container"
      $lockScroll={brandStoryVisible && !isMobile && hideMetadata}
      onClick={handleClickInsidePage}
    >
      <Head>{showNoindexTag && <meta name="robots" content="noindex" />}</Head>
      <SearchGrid.Header
        id="header"
        $isHeaderVisible={isHeaderVisible}
        $isStickyAndSuperCompressed={superCompressHeader || hideMetadata}
        $isTimelinePath={isTimelinePath}
      >
        {(!isMobile || (description && description?.length > 0)) && (
          <GridHeader
            count={resultsCount}
            description={timelineDescription || description}
            hasLink={hasLink}
            hideMetadata={hideMetadata}
            isHeaderVisible={isHeaderVisible}
            isSearchPath={isSearchPath}
            isStickyAndSuperCompressed={superCompressHeader || hideMetadata}
            isTimelinePath={isTimelinePath}
            linkDestination={linkDestination}
            linkTitle={linkTitle}
            resultIds={resultIds}
            title={isTimelinePath ? timelineTitle : title || searchQuery || 'Shop All'}
          />
        )}
        {isMobile && (
          <SearchGrid.SearchSortFiltersAndHeaderMobileWrapper
            $alwaysCompressHeader={
              (isHeaderVisible && alwaysCompressHeader) || (isHeaderVisible && hideMetadata)
            }
            $showFilters={isHeaderVisible && areFiltersVisible}
            $shouldShowFiltersAtTop={!isHeaderVisible}
            $isBannerVisible={isBannerVisible}
            $isBranchBannerActive={isBranchBannerActive}
            $branchBannerHeight={branchBannerHeight}
          >
            <GridHeader
              count={resultsCount}
              description={timelineDescription || description}
              hasLink={hasLink}
              isSearchPath={isSearchPath}
              isStickyAndSuperCompressed
              isTimelinePath={isTimelinePath}
              linkDestination={linkDestination}
              linkTitle={linkTitle}
              resultIds={resultIds}
              title={isTimelinePath ? timelineTitle : title || searchQuery || 'Shop All'}
            />
            <SearchGrid.SearchSortAndFiltersMobileWrapper data-qa="mobile_sort_filter_wrapper">
              <SearchSortAndFiltersMobile
                facets={facets}
                groups={groups}
                resultIds={resultIds}
                sortOptions={sortOptions}
              />
            </SearchGrid.SearchSortAndFiltersMobileWrapper>
          </SearchGrid.SearchSortFiltersAndHeaderMobileWrapper>
        )}
        {isTimelinePath && (
          <TimelineNavigation
            timelineYears={timelineYears}
            showTimelineNavigationOnMobile={isHeaderVisible}
          />
        )}
      </SearchGrid.Header>
      <SearchGrid.Body
        id="grid-body"
        $alwaysCompressHeader={alwaysCompressHeader}
        $showExpandedHeader={description && description?.length > 0 && !isTimelinePath}
        $isStickyAndSuperCompressed={superCompressHeader}
        $isTimelinePath={isTimelinePath}
        $isBrandStoryGrid={hideMetadata}
      >
        <SearchGrid.LeftAside $showFilters>
          {!isMobile && (
            <SearchSortAndFiltersDesktop
              facets={facets}
              groups={groups}
              hideMetadata={hideMetadata}
              sortOptions={sortOptions}
              isStickyAndSuperCompressed={superCompressHeader}
              isTimelinePath={isTimelinePath}
              resultIds={resultIds}
            />
          )}
          {breadcrumbs && !isMobile && (
            <SearchGrid.BreadcrumbContainer
              id="grid-breadcrumbs"
              $isOverlapping={isFooterVisible}
              $isVisible={areBreadcrumbsVisible && areFiltersVisible}
            >
              <SearchGridBreadcrumbs breadcrumbs={breadcrumbs} />
            </SearchGrid.BreadcrumbContainer>
          )}
        </SearchGrid.LeftAside>
        <SearchGrid.Wrapper>
          {isScavengerHuntEnabled && <ScavengerHuntFoundIndicator />}
          {results.length === 0 ? (
            <SearchNoResults />
          ) : (
            <Grid
              browseKey={browseKey}
              browseValue={browseValue}
              currencyRates={currencyRates}
              disableLazyLoad={hideMetadata && !isMobile}
              globalPricingId={globalPricingId}
              products={results}
              resultsCount={resultsCount}
            />
          )}
          <SearchGrid.Observer ref={observerRef} />
          {results?.length < resultsCount && (
            <Loader style={{ width: '40%', margin: '40px auto' }} />
          )}
          {breadcrumbs && isMobile && (
            <SearchGrid.MobileBreadcrumbContainer>
              <SearchGridBreadcrumbs breadcrumbs={breadcrumbs} />
            </SearchGrid.MobileBreadcrumbContainer>
          )}
        </SearchGrid.Wrapper>
        <SearchGrid.RightAside>
          <SearchGrid.ScrollToTop $isVisible={showScrollToTopArrow}>
            {!hideMetadata && <ScrollToTopArrow />}
          </SearchGrid.ScrollToTop>
        </SearchGrid.RightAside>
      </SearchGrid.Body>
      {hideMetadata && !isMobile && <SearchGrid.FillerEllement />}
    </SearchGrid.Container>
  );
};

SearchGrid.Container = styled.div<{ $lockScroll: boolean }>`
  position: ${({ $lockScroll }) => ($lockScroll ? `fixed` : `relative`)};
  width: 100%;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
`;

SearchGrid.Header = styled.div<{
  $isHeaderVisible?: boolean;
  $isStickyAndSuperCompressed?: boolean;
  $isTimelinePath?: boolean;
}>`
  background: ${({ theme }) => theme.navigation.backgroundColor};
  top: ${({ $isHeaderVisible }) => ($isHeaderVisible ? NEW_NAV_MOBILE_HEADER_HEIGHT : `0px`)};
  z-index: ${({ theme }) => theme.zIndex.searchGridHeader};

  ${({ $isStickyAndSuperCompressed, $isTimelinePath }) =>
    $isTimelinePath && $isStickyAndSuperCompressed && `position: relative;`};
  ${({ $isTimelinePath }) => $isTimelinePath && `border-bottom: none;`};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    border-bottom: none;
    margin: auto;
    position: relative;
    top: 0;
    width: 100%;
  }
`;

SearchGrid.SearchSortFiltersAndHeaderMobileWrapper = styled.div<{
  $alwaysCompressHeader: boolean;
  $branchBannerHeight?: string;
  $isBannerVisible?: boolean;
  $isBranchBannerActive?: boolean;
  $shouldShowFiltersAtTop?: boolean;
  $showFilters: boolean;
}>`
  ${showOnMobileAndTabletOnly`
    background: ${({ theme }) => theme.navigation.backgroundColor};
    margin-bottom: 18px;
    border-bottom: 1px solid ${({ theme }) => theme.navigation.borderColor};
    overflow: visible;
    min-height: 30px;
    position: fixed;
    top: ${({
      $alwaysCompressHeader,
      $isBannerVisible,
      $isBranchBannerActive,
      $branchBannerHeight,
      $showFilters,
      $shouldShowFiltersAtTop,
    }) => {
      const height = `calc(${NEW_NAV_MOBILE_HEADER_HEIGHT}
        + ${$isBannerVisible ? MOBILE_BANNER_HEIGHT : '0px'}
        + ${$isBranchBannerActive ? String($branchBannerHeight) : '0px'})`;

      if ($shouldShowFiltersAtTop) {
        return '-30px';
      }

      return $showFilters || $alwaysCompressHeader ? height : `-130px`;
    }};
    padding: 16px 0px;
    width: 100%;

    z-index: ${({ theme }) => theme.zIndex.searchGridHeader};
    transition: all 0.3s ease-in;
    ${hideScrollbars};
  `};
`;

SearchGrid.SearchSortAndFiltersMobileWrapper = styled.div`
  width: 100vw;
  white-space: nowrap;
  overflow-x: scroll;
  ${hideScrollbars};
`;

SearchGrid.Body = styled.div<{
  $alwaysCompressHeader: boolean;
  $showExpandedHeader: boolean;
  $isStickyAndSuperCompressed: boolean;
  $isTimelinePath: boolean;
  $isBrandStoryGrid: boolean;
}>`
  border-top: 1px solid ${({ theme }) => theme.navigation.borderColor};
  position: relative;
  display: flex;
  margin-bottom: ${MOBILE_HEADER_HEIGHT};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${({ $alwaysCompressHeader }) => $alwaysCompressHeader && `top: 60px;`};
    ${({ $isBrandStoryGrid }) => $isBrandStoryGrid && `top: 104px;`};
    ${({ $isTimelinePath }) => $isTimelinePath && `top: 111px; margin-bottom: 84px;`}
    ${({ $showExpandedHeader }) => !$showExpandedHeader && `top: 120px;`};
    ${({ $isTimelinePath, $showExpandedHeader }) =>
      $isTimelinePath && !$showExpandedHeader && `top: 140px;`};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    border-top: none;
  }

  ${({ $isStickyAndSuperCompressed }) => $isStickyAndSuperCompressed && `margin-top: 0px;`}
`;

SearchGrid.Wrapper = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: ${GRIDS_WIDTH};
  }
`;

SearchGrid.ScrollToTop = styled.div<{ $isVisible: boolean }>`
  ${showOnDesktopOnly`
    position: sticky;
    align-self: flex-end;
    bottom: 0;
    padding: 38px 14px;
    opacity: 0;
    visibility: hidden;
    ${({ $isVisible }) => $isVisible && toggleVisible};
  `}
`;

SearchGrid.Observer = styled.div`
  position: absolute;
  bottom: 0;
  height: 900px;
`;

SearchGrid.Aside = styled.aside<{ $showFilters?: boolean }>`
  ${showOnDesktopOnly`
    width: calc((100% - ${GRIDS_WIDTH})/2);
  `}
`;

SearchGrid.LeftAside = styled(SearchGrid.Aside)`
  ${showOnDesktopOnly`
    opacity: ${({ $showFilters }) => ($showFilters ? `1` : `0`)};
    transition: all 0.2s ease-in;
  `}
`;

SearchGrid.RightAside = styled(SearchGrid.Aside)`
  ${showOnDesktopOnly`
    display: flex;
    justify-content: flex-end;
  `}
`;

SearchGrid.BreadcrumbContainer = styled.div<{ $isOverlapping?: boolean; $isVisible: boolean }>`
  ${({ $isOverlapping }) => showOnDesktopOnly`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
    padding-right: 16px;
    ${$isOverlapping ? `position: absolute;` : `position: fixed;`}
    text-align: right;
    width: calc((100% - ${GRIDS_WIDTH})/2);
  `};

  transition: all 0.3s;
  transition-timing-function: ease-in;

  ${({ $isVisible, $isOverlapping }) =>
    $isVisible
      ? `opacity: 1; ${
          $isOverlapping
            ? `
    bottom: 0px;`
            : `
    bottom: 36px;`
        } `
      : 'opacity: 0; bottom: -300px;'}
`;

SearchGrid.MobileBreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  margin-bottom: 72px;
  margin-top: 24px;
  padding-left: 16px;
`;

SearchGrid.FillerEllement = styled.div`
  height: 50px;
  width: 100vw;
`;
